import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Form, Pagination } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { fulfillmentAPI } from '../utils/api';

const FulfillmentDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const loadOrders = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      const params = {
        page,
        limit
      };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await fulfillmentAPI.getReadyOrders(params);
      setOrders(res.data.data);
      setCurrentPage(res.data.page);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleFilter = () => {
    setCurrentPage(1);
    loadOrders(1);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadOrders(page);
  };

  const handleStartPicking = async (orderId) => {
    try {
      await fulfillmentAPI.startPicking(orderId);
      navigate(`/picking/${orderId}`);
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการเริ่มเบิกสินค้า');
    }
  };

  const getFulfillmentStatusBadge = (status) => {
    const statusMap = {
      pending: { variant: 'info', text: 'พร้อมเบิก' },
      reserved: { variant: 'info', text: 'พร้อมเบิก' },
      picking: { variant: 'warning', text: 'กำลังเบิก' },
      fulfilled: { variant: 'success', text: 'เบิกแล้ว' }
    };
    const config = statusMap[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-box-seam me-2"></i>
            จัดการเบิกสินค้า
          </h2>
          <p className="text-muted">เบิกสินค้าสำหรับคำสั่งซื้อ</p>
        </Col>
        <Col xs="auto">
          <Link to="/fulfilled-orders">
            <Button variant="info">
              <i className="bi bi-check-circle me-2"></i>
              ดูคำสั่งซื้อที่เบิกแล้ว
            </Button>
          </Link>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Date Filter */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>วันที่เริ่มต้น</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>วันที่สิ้นสุด</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button variant="primary" onClick={handleFilter} className="me-2">
                <i className="bi bi-search me-1"></i>
                ค้นหา
              </Button>
              <Button variant="outline-secondary" onClick={handleClearFilter}>
                <i className="bi bi-x-circle me-1"></i>
                ล้าง
              </Button>
            </Col>
            <Col md={3} className="text-end">
              <span className="text-muted">พบทั้งหมด {total} รายการ</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h5 className="mb-0">คำสั่งซื้อที่พร้อมเบิก</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
              <p className="text-muted mt-3">ไม่มีคำสั่งซื้อที่พร้อมเบิก</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>คำสั่งซื้อ #</th>
                  <th>สาขา</th>
                  <th>วันที่สั่ง</th>
                  <th>รายการ</th>
                  <th>สถานะ</th>
                  <th>ผู้เบิก</th>
                  <th>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <Link to={`/orders/${order.id}`}>
                        <strong>#{order.id}</strong>
                      </Link>
                    </td>
                    <td>{order.orderedBy.fullName}</td>
                    <td>
                      {new Date(order.createdAt).toLocaleDateString('th-TH')}
                    </td>
                    <td>
                      {order.items.length} รายการ
                    </td>
                    <td>
                      {getFulfillmentStatusBadge(order.fulfillmentStatus)}
                    </td>
                    <td>
                      {order.fulfillment?.fulfilledBy?.fullName || '-'}
                    </td>
                    <td>
                      {(order.fulfillmentStatus === 'pending' || order.fulfillmentStatus === 'reserved') ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleStartPicking(order.id)}
                        >
                          <i className="bi bi-play-circle me-1"></i>
                          เริ่มเบิก
                        </Button>
                      ) : order.fulfillmentStatus === 'picking' ? (
                        <Link to={`/picking/${order.id}`}>
                          <Button variant="warning" size="sm">
                            <i className="bi bi-arrow-right-circle me-1"></i>
                            ดำเนินการต่อ
                          </Button>
                        </Link>
                      ) : order.fulfillmentStatus === 'fulfilled' ? (
                        <Badge bg="success">เบิกแล้ว</Badge>
                      ) : (
                        <Badge bg="secondary">-</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {/* Pagination */}
        {totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <span className="text-muted">
              หน้า {currentPage} จาก {totalPages}
            </span>
            <Pagination className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, idx) => {
                const page = idx + 1;
                // Show limited page numbers around current page
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                } else if (
                  page === currentPage - 3 ||
                  page === currentPage + 3
                ) {
                  return <Pagination.Ellipsis key={page} disabled />;
                }
                return null;
              })}
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>
    </Container>
  );
};

export default FulfillmentDashboard;
